import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api, clinicHeader } from "../api/client";
import { getWho } from "../api/session";
import { QuickActions } from "../components/QuickActions";
import { Sparkline } from "../components/Sparkline";
import { useSession } from "../useSession";

type Queued = {
  id: string;
  patient_id: string;
  patient_name: string;
  status: "arrived" | "in_progress";
  waiting_minutes: number;
  seen_by_name: string;
};

type Appointment = {
  id: string;
  patient_id?: string;
  patient_name?: string;
  start_datetime: string;
  status: string;
  staff_name?: string;
  reason?: string;
};

type Result = { id: string; patient_name: string; waiting_hours: number; display?: string };
type Escalation = {
  escalation_id: string;
  patient_id: string;
  patient_name: string;
  reason: string;
  severity: string;
  created_at: string;
};
type Week = { week: string; booked: number; completed: number; no_show: number };
type Slot = { id: string; start_datetime: string; staff_name: string; status: string };

function clockTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/* The same wording the two worklists use, so the dashboard cannot age the
   same row differently from the screen it links to. A patient who has been
   here twelve hours reads as "12h 38m" on both, never as "758 min". */

function resultAge(hours: number) {
  if (hours < 1) return "under an hour";
  if (hours < 48) return `${Math.round(hours)} hours`;
  return `${Math.round(hours / 24)} days`;
}

function queueAge(minutes: number) {
  if (minutes < 1) return "just arrived";
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

const GLYPH: Record<string, string> = {
  clinic: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8M4 20c0-3.3 3.6-5 8-5s8 1.7 8 5",
  flask: "M9 3v6l-4.5 9A2 2 0 0 0 6.3 21h11.4a2 2 0 0 0 1.8-3L15 9V3M9 3h6",
  calendar: "M4 7h16v13H4zM4 7V5h16v2M9 3v4M15 3v4",
  bell: "M18 9a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7M13.7 20a2 2 0 0 1-3.4 0",
  search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3",
  message: "M4 5h16v10H9l-5 4z",
  poster: "M7 3h10v18H7zM10 7h4M10 11h4M10 15h2",
  chart: "M4 20h16M7 20v-7M12 20V6M17 20v-4",
  recall: "M20 12a8 8 0 1 1-2.3-5.6M20 4v4h-4",
  person: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8M4 20c0-3.3 3.6-5 8-5s8 1.7 8 5",
  note: "M6 3h9l3 3v15H6zM14 3v4h4M9 12h6M9 16h4",
  tick: "M20 6 9 17l-5-5",
  cross: "M6 6l12 12M18 6 6 18",
  chevron: "m9 5 7 7-7 7",
};

function Glyph({ name }: { name: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d={GLYPH[name]} />
    </svg>
  );
}

/**
 * How big the unread-results backlog was on each of the last seven days.
 *
 * Derivable from what is already on screen and needs no new table: a result
 * that has been waiting 26 days was also waiting six days ago, so counting the
 * ones older than each day gives the backlog on that day. It is a real
 * reading, not a drawing.
 *
 * The other three counts have no equivalent. A queue empties and leaves no
 * trace of its depth, so their trend stays absent until something records it.
 */
/**
 * A date the API will accept, read off the browser's own clock.
 *
 * Not `toISOString().slice(0, 10)`, which is the date in UTC: for a clinic at
 * UTC+8 every evening after eight would ask the server for yesterday, and the
 * schedule card would fill with a day that has already happened.
 *
 * The browser's day and the clinic's day are the same day for a desk standing
 * in the clinic, which is who this screen is for, and it is the same clock the
 * card already measures its own cut-off by.
 */
function localDate(at: Date) {
  const month = String(at.getMonth() + 1).padStart(2, "0");
  const day = String(at.getDate()).padStart(2, "0");
  return `${at.getFullYear()}-${month}-${day}`;
}

function backlogByDay(results: Result[] | null, days = 7): number[] | null {
  if (!results || results.length === 0) return null;
  const series: number[] = [];
  for (let back = days - 1; back >= 0; back--) {
    const threshold = back * 24;
    series.push(results.filter((row) => row.waiting_hours >= threshold).length);
  }
  return series;
}

/**
 * Which pastel an avatar wears.
 *
 * Derived from the name, so one person is the same colour every time the
 * screen is drawn and two people beside each other are rarely the same. It
 * carries no meaning and must not: it is there so a column of initials reads
 * as a list of people rather than as a column of discs.
 */
function tintOf(name: string) {
  let sum = 0;
  for (const ch of name) sum = (sum + ch.charCodeAt(0)) % 997;
  return sum % 6;
}

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");
}

/**
 * The first screen after signing in.
 *
 * Four counts and the three lists behind them. Every number is a link: a
 * dashboard that reports a problem and leaves you to find the screen that
 * fixes it has made more work, not less.
 *
 * ## What the mockup has that this does not
 *
 * The approved mockup carries patient photographs, room numbers, a "vs
 * yesterday" delta under each count with a sparkline, and message previews on
 * the dashboard. None of them are here, and each for its own reason rather
 * than for want of time.
 *
 * - There is no patient photograph anywhere in the product, so initials.
 * - Rooms are not modelled. An invented room number on a clinical screen is
 *   worse than no room number.
 * - Nothing records yesterday's counts, so a delta would be a drawing.
 * - A message preview on the dashboard would put a patient's words on the
 *   first screen anybody opens, which is the one thing the escalation list is
 *   built to avoid: it carries no message text (ADR-008).
 *
 * The brief that governs these screens says the mockup is a reference and
 * that invented controls are not to be reproduced. These are that rule
 * applied.
 */
export function Dashboard() {
  const { clinic } = useSession();
  const [queue, setQueue] = useState<Queued[] | null>(null);
  const [today, setToday] = useState<Appointment[] | null>(null);
  const [results, setResults] = useState<Result[] | null>(null);
  const [escalations, setEscalations] = useState<Escalation[] | null>(null);
  const [weeks, setWeeks] = useState<Week[] | null>(null);
  const [slots, setSlots] = useState<Slot[] | null>(null);

  const load = useCallback(async () => {
    const header = clinicHeader();
    // Four reads, together. Sequentially this is the slowest screen in the
    // portal; in parallel it is the speed of the slowest one.
    const [q, a, r, e, w, sl] = await Promise.all([
      api.GET("/portal/queue", { params: { header } }),
      api.GET("/portal/appointments", { params: { header, query: {} } }),
      api.GET("/portal/results/unacknowledged", { params: { header } }),
      api.GET("/portal/escalations", { params: { header } }),
      api.GET("/portal/reports/attendance", { params: { header, query: { weeks: 7 } } }),
      // Today's openings, asked for as a day.
      //
      // This asked for a number and took the server's default hundred, which
      // then decided where the schedule below stopped. A day holds as many
      // slots as the doctors on duty and their hours make it hold — two
      // doctors around the clock is 192 at quarter-hour appointments — so any
      // number is a guess, and when the guess is short the card simply ends
      // early. That is the worst way for a schedule to be wrong: an evening
      // that is missing and an evening that is free look the same, and
      // nothing on the screen says which one this is.
      //
      // `date` bounds both ends of one clinic day, so there is no number left
      // to get wrong. The limit stays as a ceiling and no longer decides
      // anything.
      api.GET("/portal/slots", {
        params: { header, query: { date: localDate(new Date()), limit: 1000 } },
      }),
    ]);
    // A role that may not read one of these gets a refusal, which is the rule
    // rather than a fault: that panel stays empty and the rest still works.
    setQueue((q.data?.items ?? []) as Queued[]);
    setToday((a.data?.items ?? []) as Appointment[]);
    setResults((r.data?.items ?? []) as Result[]);
    // This one hands back the array itself rather than wrapping it, which the
    // generated types catch and a hand-written call would not have.
    setEscalations((e.data ?? []) as unknown as Escalation[]);
    setWeeks((w.data?.items ?? []) as Week[]);
    setSlots((sl.data?.items ?? []) as Slot[]);
  }, []);

  useEffect(() => {
    void load();
    // A waiting room changes while somebody is looking at it.
    const timer = setInterval(() => void load(), 60_000);
    return () => clearInterval(timer);
  }, [load]);

  const waiting = (queue ?? []).filter((row) => row.status === "arrived");

  const now = new Date();

  /* What is left of the day, not what the day began with.
   *
   * At ten to one the schedule was still showing half past eight. A list of
   * appointments that have already happened is a record, and the dashboard is
   * meant to be the next thing to do. Anything whose slot has passed drops
   * off; `started` keeps the count honest when nothing is left.
   */
  const started = (today ?? []).length;

  /* The rest of the day, booked and free together.
   *
   * A list of only the bookings answers "who is coming" and leaves out the
   * other half of what somebody looking at a clinic's afternoon wants, which
   * is where the gaps are. The front desk reads this with a patient on the
   * telephone. So the two lists are merged on the clock: an appointment where
   * there is one, an open time where there is not.
   *
   * Only today, and only from a quarter hour ago, because a slot that has
   * passed is neither a plan nor an opening.
   */
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 59, 999);
  const from = now.getTime() - 15 * 60_000;

  const timeline = [
    ...(today ?? [])
      .filter((row) => {
        const at = new Date(row.start_datetime).getTime();
        return at >= from && at <= endOfDay.getTime();
      })
      .map((row) => ({ kind: "booked" as const, at: row.start_datetime, row })),
    ...(slots ?? [])
      .filter((slot) => {
        const at = new Date(slot.start_datetime).getTime();
        return slot.status === "open" && at >= from && at <= endOfDay.getTime();
      })
      .map((slot) => ({ kind: "open" as const, at: slot.start_datetime, slot })),
  ].sort((a, b) => a.at.localeCompare(b.at));
  const greeting = now.getHours() < 12 ? "Good morning" : now.getHours() < 18 ? "Good afternoon" : "Good evening";
  const who = getWho();

  const resultsTrend = backlogByDay(results);
  // Destructured rather than indexed, so the compiler can see that both ends
  // exist before they are subtracted.
  const [previous, latest] = resultsTrend ? resultsTrend.slice(-2) : [];
  const trendDelta =
    previous !== undefined && latest !== undefined ? latest - previous : null;

  const tiles = [
    {
      to: "/queue",
      icon: "clinic",
      tone: "clinic",
      spark: "green" as const,
      n: waiting.length,
      label: "In the clinic",
      ready: queue !== null,
      // A queue empties and leaves nothing behind saying how deep it was.
      trend: null,
      delta: null,
    },
    {
      to: "/results",
      icon: "flask",
      tone: "results",
      spark: "red" as const,
      n: (results ?? []).length,
      label: "Results waiting",
      ready: results !== null,
      trend: resultsTrend,
      delta: trendDelta,
    },
    {
      to: "/appointments",
      icon: "calendar",
      tone: "",
      spark: "gold" as const,
      n: (today ?? []).length,
      label: "Today's appointments",
      ready: today !== null,
      trend: null,
      delta: null,
    },
    {
      to: "/messages",
      icon: "bell",
      tone: "waiting",
      spark: "blue" as const,
      n: (escalations ?? []).length,
      label: "Needs a reply",
      ready: escalations !== null,
      trend: null,
      delta: null,
    },
  ];

  return (
    <section className="dash">
      <div className="hero">
        <div className="hero-photo" aria-hidden="true" />
        <div className="hero-words">
          <p className="hero-eyebrow">{greeting}</p>
          <h1>{who ?? clinic?.name}</h1>
          {/* The clinic under the name, but not under itself: a session that
              signed in before the name existed has only the clinic, and
              printing it twice reads as a bug. */}
          {who && <p className="hero-sub">{clinic?.name}</p>}
        </div>
        <p className="hero-eyebrow-right">
          Intelligent care
          <br />
          redefined
          {/* The day, in its own block.

              "redefined" above is a bare text node, so a date set to anything
              inline joins that line rather than starting one: it landed
              beside the word and, in a window narrower than the one it was
              checked in, on top of it. This wrapper is what makes the break,
              which leaves the date and the clock free to be laid out against
              each other without deciding where the line begins. */}
          <span className="hero-when">
            <span className="hero-date">
              {now.toLocaleDateString("en-MY", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
            </span>
            <span className="hero-clock">
              {now.toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit", hour12: false })}
            </span>
          </span>
        </p>
      </div>

      <div className="tiles">
        {tiles.map((tile) => (
          <Link key={tile.to} to={tile.to} className={`tile ${tile.tone}`.trim()}>
            <span className="tile-mark"><Glyph name={tile.icon} /></span>
            <span className="tile-words">
              <span className="tile-n">{tile.ready ? tile.n : "—"}</span>
              <span className="tile-label">{tile.label}</span>
              <span className="tile-trend">
                <span
                  className={`tile-delta ${
                    tile.delta === null
                      ? "none"
                      : tile.delta === 0
                        ? ""
                        : tile.delta > 0
                          ? "down"
                          : "up"
                  }`.trim()}
                >
                  {/* Three different things, and each says which it is. A
                      count that has not moved and a count nothing has been
                      recorded for are not the same, and a blank space claims
                      to be neither. */}
                  {tile.delta === null
                    ? "No history yet"
                    : tile.delta === 0
                      ? "No change since yesterday"
                      : `${tile.delta > 0 ? "+" : ""}${tile.delta} since yesterday`}
                </span>
                <Sparkline points={tile.trend} tone={tile.spark} />
              </span>
            </span>
          </Link>
        ))}
      </div>

      <div className="panels">
        <article className="panel">
          <header>
            <h2>Today's schedule</h2>
            <Link to="/appointments">View all</Link>
          </header>

          {today !== null && slots !== null && timeline.length === 0 && (
            <p className="empty">
              {started === 0
                ? "Nothing booked today, and no times left open."
                : `All ${started} have been and gone, and nothing is open.`}
            </p>
          )}

          <ul>
            {timeline.map((entry) => (
              <li key={entry.kind === "open" ? entry.slot.id : entry.row.id} className="slot">
                <span className="slot-when">{clockTime(entry.at)}</span>
                <span className="slot-rail" aria-hidden="true" />

                {/* An opening is drawn as an opening: the same height and the
                    same rail, plainly empty, so a gap in the afternoon can be
                    seen rather than counted. */}
                {entry.kind === "open" ? (
                  <span className="slot-card open">
                    <span className="slot-free">Open</span>
                    <span className="slot-meta">
                      <Glyph name="person" />
                      {entry.slot.staff_name}
                    </span>
                  </span>
                ) : (
                  <Link to={`/patients/${entry.row.patient_id ?? ""}`} className="slot-card">
                    <span
                      className={`slot-face tint-${tintOf(entry.row.patient_name ?? "")}`}
                      aria-hidden="true"
                    >
                      {initials(entry.row.patient_name ?? "")}
                    </span>

                    <span className="slot-words">
                      <span className="slot-name">{entry.row.patient_name ?? "A patient"}</span>
                      {entry.row.staff_name && (
                        <span className="slot-meta">
                          <Glyph name="person" />
                          {entry.row.staff_name}
                        </span>
                      )}
                      {entry.row.reason && (
                        <span className="slot-reason">
                          <Glyph name="note" />
                          <span>{entry.row.reason}</span>
                        </span>
                      )}
                    </span>

                    <span className={`slot-state ${entry.row.status}`}>
                      <Glyph name={entry.row.status === "no_show" ? "cross" : "tick"} />
                      {entry.row.status.replace(/_/g, " ")}
                    </span>
                    <Glyph name="chevron" />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </article>

        <article className="panel">
          <header>
            <h2>Results waiting</h2>
            <Link to="/results">View all</Link>
          </header>
          {results?.length === 0 && <p className="empty">Every result has been read.</p>}
          <ul>
            {(results ?? []).slice(0, 6).map((row) => (
              <li key={row.id}>
                <span className="who">
                  <span className="name">{row.patient_name}</span>
                  {row.display && <span className="sub">{row.display}</span>}
                </span>
                <span className="ago">{resultAge(row.waiting_hours)}</span>
              </li>
            ))}
          </ul>
        </article>

        <article className="panel">
          <header>
            <h2>In the clinic</h2>
            <Link to="/queue">View all</Link>
          </header>
          {queue?.length === 0 && <p className="empty">Nobody is here yet.</p>}
          <ul>
            {(queue ?? []).slice(0, 6).map((row) => (
              <li key={row.id}>
                <span className="avatar small" aria-hidden="true">{initials(row.patient_name)}</span>
                <span className="who">
                  <Link to={`/patients/${row.patient_id}`} className="name">{row.patient_name}</Link>
                  <span className="sub">
                    {row.status === "in_progress" ? `with ${row.seen_by_name || "a clinician"}` : "waiting"}
                  </span>
                </span>
                <span className="ago">
                  {row.status === "in_progress" ? "—" : queueAge(row.waiting_minutes)}
                </span>
              </li>
            ))}
          </ul>
        </article>

        {/* The mockup puts a message preview on each row. This carries who and
            why and no message text, because the escalation list is built not
            to: a patient's words do not belong on the first screen anybody
            opens (ADR-008). Opening the conversation is one click away. */}
        <article className="panel">
          <header>
            <h2>Needs a reply</h2>
            <Link to="/messages">View all</Link>
          </header>
          {escalations?.length === 0 && (
            <p className="empty">
              Nobody is waiting. Conversations the assistant hands over appear here.
            </p>
          )}
          <ul>
            {(escalations ?? []).slice(0, 4).map((row) => (
              <li key={row.escalation_id}>
                <span className="avatar small" aria-hidden="true">{initials(row.patient_name)}</span>
                <span className="who">
                  <Link to={`/patients/${row.patient_id}`} className="name">{row.patient_name}</Link>
                  <span className="sub">{row.reason.replace(/_/g, " ")}</span>
                </span>
                <span className="ago">{clockTime(row.created_at)}</span>
              </li>
            ))}
          </ul>
        </article>

        {/* The mockup's chart is seven days with three series. The clinic
            records weeks, not days, so this is weeks: the same question asked
            of the data that exists rather than of the data in the drawing. */}
        <article className="panel">
          <header>
            <h2>At a glance</h2>
            <Link to="/attendance">View all</Link>
          </header>
          {weeks?.length === 0 && (
            <p className="empty">
              Nothing to show yet. Weeks appear here once appointments have been
              marked as attended or missed.
            </p>
          )}
          {weeks && weeks.length > 0 && (
            <>
              <div className="glance">
                {weeks.map((week) => {
                  const tallest = Math.max(1, ...weeks.map((one) => one.booked));
                  const height = (value: number) => `${Math.round((value / tallest) * 100)}%`;
                  return (
                    <div key={week.week} className="glance-week">
                      <div className="glance-bars">
                        <i className="glance-bar booked" style={{ height: height(week.booked) }} />
                        <i className="glance-bar completed" style={{ height: height(week.completed) }} />
                        <i className="glance-bar missed" style={{ height: height(week.no_show) }} />
                      </div>
                      <span className="glance-label">W{week.week.split("-W")[1]}</span>
                    </div>
                  );
                })}
              </div>
              <p className="glance-key">
                <span><i className="glance-bar booked" /> Booked</span>
                <span><i className="glance-bar completed" /> Completed</span>
                <span><i className="glance-bar missed" /> Missed</span>
              </p>
            </>
          )}
        </article>

        <QuickActions />
      </div>
    </section>
  );
}
